import { Event } from '../models/event'
import { EventProperty } from '../models/event-property'
import { EVENT_PROPERTY_KEY } from '~/lib/constants/event'
import { BasicEventInformation } from '~/lib/models/basic-event-information'
import { normalizeQrStyle } from '~/lib/qr/qr-style'

export function mapEvents(events, filesPath) {
  return events.map((event) => mapEvent(event, filesPath))
}

export function mapEvent(event, filesPath) {
  return new Event({
    id: event.id,
    name: event.name,
    status: event.status,
    safeName: event.safeName,
    date: new Date(event.epochDateTime),
    publicationDate: event.publicationEpochDateTime ? new Date(event.publicationEpochDateTime) : null,
    publicationExpiresAt: event.publicationExpiresAtEpochDateTime
      ? new Date(event.publicationExpiresAtEpochDateTime)
      : null,
    shortLink: event.shortLink,
    properties: event.properties ? mapEventProperties(event.properties, filesPath) : [],
    albums: event.albums ? mapAlbums(event.albums, filesPath) : [],
    qrStyle: mapQrStyle(event.qrStyle, filesPath),
  })
}

/**
 * The host's QR style as the API sends it on owner responses. `photoUrl` is a
 * signed CDN url when the CDN is configured; otherwise the stored path is served
 * from filesPath, the same way backgroundImage paths are (mapValue above).
 * @param {object|null|undefined} raw
 * @param {string} filesPath
 * @returns {QrStyle|null}
 */
export function mapQrStyle(raw, filesPath) {
  if (!raw) {
    return null
  }

  const style = normalizeQrStyle(raw)

  if (style.image.photo && !style.image.photoUrl) {
    style.image.photoUrl = `${filesPath}/${style.image.photo}`
  }

  return style
}

export function mapAlbums(albums, filesPath) {
  return albums.map((album) => ({
    id: album.id,
    name: album.name,
    slug: album.slug,
    shortLink: album.shortLink ?? null,
    // Per-album sorting override; null = inherits the event's setting.
    gallerySorting: album.gallerySorting ?? null,
    // Private/separate: hidden from guest chip strips; its direct link shows
    // a locked view (no filters) with uploads forced into the album.
    isPrivate: album.isPrivate ?? false,
    // Own QR style (owner responses only); null = follows the event's style.
    qrStyle: mapQrStyle(album.qrStyle, filesPath),
  }))
}

export function mapEventProperties(eventProperties, filesPath) {
  return eventProperties.map((property) => mapEventProperty(property, filesPath))
}

export function mapEventProperty(property, filesPath) {
  return new EventProperty({
    id: property.id,
    key: property.key,
    value: mapValue(property.key, property.value, filesPath),
    mobileValue: mapValue(property.key, property.mobileValue, filesPath),
    validUntil: new Date(property.validUntil),
  })
}

function mapValue(key, value, filesPath) {
  const fileKeys = [
    EVENT_PROPERTY_KEY.BACKGROUND_IMAGE_UPLOAD,
    EVENT_PROPERTY_KEY.BACKGROUND_IMAGE,
    EVENT_PROPERTY_KEY.ORIGINAL_BACKGROUND_IMAGE,
  ]

  if (!value) return value

  if (!fileKeys.includes(key)) {
    return value
  }

  if (value?.includes('/themes')) {
    return value
  }

  return `${filesPath}/${value}`
}

export function mapBasicEventInformation(basicEventInformation) {
  return new BasicEventInformation({
    id: basicEventInformation.id,
    name: basicEventInformation.name,
    status: basicEventInformation.status,
    date: new Date(basicEventInformation.epochDateTime),
  })
}
